import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  PAID_REPORT_PAGE_SIZE,
  buildClientPaidReportQuery,
  buildPaidPerformanceFetchKey,
  formatPaidPerformanceDate,
  paidReportRequestPath,
  unwrapPaidPerformancePayload,
} from "../../lib/utils/paidPerformanceReport"

function paidLevelName(level) {
  if (level === "campaign" || level === "objective") return level
  return "ad"
}

function paidArgKey(arg = {}) {
  const {
    clientId,
    startDate,
    endDate,
    level = "ad",
    sortBy = "",
    id = "all",
  } = arg
  return buildPaidPerformanceFetchKey(
    clientId,
    formatPaidPerformanceDate(startDate),
    formatPaidPerformanceDate(endDate),
    { level: paidLevelName(level), sortBy, id },
  )
}

function paidScopeKey(arg = {}) {
  const { clientId, startDate, endDate } = arg
  return `${String(clientId ?? "")}|${formatPaidPerformanceDate(startDate)}|${formatPaidPerformanceDate(endDate)}`
}

function emptyPaidPaging() {
  return { after: null, next: null, hasMore: false, pageResults: 0 }
}

function emptyPagingByLevel() {
  return {
    ad: emptyPaidPaging(),
    campaign: emptyPaidPaging(),
    objective: emptyPaidPaging(),
  }
}

/**
 * GET /api/v1/report/clients/{clientId}/paid?level=&id=&start_date=&end_date=&limit=&sort_by=
 */
export const fetchClientPaidPerformance = createAsyncThunk(
  "clientPaidPerformance/fetch",
  async (
    {
      clientId,
      startDate,
      endDate,
      level = "ad",
      id = "all",
      sortBy = "",
      after,
      next,
      append = false,
      limit = PAID_REPORT_PAGE_SIZE,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatPaidPerformanceDate(startDate)
      const end = formatPaidPerformanceDate(endDate)
      const nextPath = append ? paidReportRequestPath(next) : null
      const query = buildClientPaidReportQuery({
        level,
        id,
        startDate: start,
        endDate: end,
        limit,
        sortBy,
        after: append ? after : undefined,
      })
      const data = await get(nextPath || `report/clients/${clientId}/paid?${query}`)
      const { rows, paging } = unwrapPaidPerformancePayload(data)

      return {
        rows,
        paging,
        level,
        sortBy,
        id,
        append,
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildPaidPerformanceFetchKey(clientId, start, end, {
          level: paidLevelName(level),
          sortBy,
          id,
        }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch paid performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force || arg?.append) return true
      const key = paidArgKey(arg)
      const state = getState().clientPaidPerformance
      if (arg?.prefetch) return !state?.loadedKeys?.[key]
      if (state?.loadedKeys?.[key]) return false
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  ads: [],
  campaigns: [],
  objectives: [],
  paging: emptyPaidPaging(),
  pagingByLevel: emptyPagingByLevel(),
  loadedKeys: {},
  scopeKey: null,
  level: "ad",
  sortBy: "",
  entityId: "all",
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function rowsForLevel(level, rows) {
  if (level === "campaign") return { campaigns: rows }
  if (level === "objective") return { objectives: rows }
  return { ads: rows }
}

function beginFetch(state, action) {
  if (Boolean(action.meta.arg?.prefetch)) return
  const arg = action.meta.arg || {}
  const nextKey = paidArgKey(arg)
  const nextScope = paidScopeKey(arg)
  const level = paidLevelName(arg.level)
  const append = Boolean(arg.append)
  state.status = append ? "loadingMore" : "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  state.level = level

  if (!append && state.scopeKey && state.scopeKey !== nextScope) {
    state.ads = []
    state.campaigns = []
    state.objectives = []
    state.loadedKeys = {}
    state.pagingByLevel = emptyPagingByLevel()
    state.paging = emptyPaidPaging()
    state.fetchedFetchKey = null
  }
  state.scopeKey = nextScope

  if (!append && !state.loadedKeys?.[nextKey]) {
    state.fetchedFetchKey = state.fetchedFetchKey === nextKey ? nextKey : null
    if (level === "campaign") state.campaigns = []
    else if (level === "objective") state.objectives = []
    else state.ads = []
    state.pagingByLevel[level] = emptyPaidPaging()
    state.paging = emptyPaidPaging()
  }
}

function isStaleFetch(action, state) {
  return paidArgKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")
}

const clientPaidPerformanceSlice = createSlice({
  name: "clientPaidPerformance",
  initialState,
  reducers: {
    clearClientPaidPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientPaidPerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientPaidPerformance.fulfilled, (state, action) => {
        const level = paidLevelName(action.payload?.level)
        const incoming = action.payload?.rows ?? []
        const append = Boolean(action.payload?.append)
        const prefetch = Boolean(action.meta.arg?.prefetch)
        const nextByLevel = rowsForLevel(level, incoming)

        if (action.payload?.fetchKey) {
          state.loadedKeys[action.payload.fetchKey] = true
        }

        if (prefetch || !isStaleFetch(action, state)) {
          if (level === "campaign") {
            state.campaigns = append ? [...state.campaigns, ...incoming] : nextByLevel.campaigns
          } else if (level === "objective") {
            state.objectives = append ? [...state.objectives, ...incoming] : nextByLevel.objectives
          } else {
            state.ads = append ? [...state.ads, ...incoming] : nextByLevel.ads
          }
          const paging = action.payload?.paging ?? emptyPaidPaging()
          state.pagingByLevel[level] = paging
          if (!prefetch) state.paging = paging
        }

        if (prefetch || isStaleFetch(action, state)) return

        state.status = "succeeded"
        state.level = level
        state.sortBy = action.payload?.sortBy ?? ""
        state.entityId = action.payload?.id ?? "all"
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientPaidPerformance.rejected, (state, action) => {
        const prefetch = Boolean(action.meta.arg?.prefetch)
        if (prefetch) {
          const key = paidArgKey(action.meta.arg || {})
          if (key) state.loadedKeys[key] = true
          return
        }
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch paid performance"
        const level = paidLevelName(action.meta.arg?.level)
        if (!action.meta.arg?.append) {
          if (level === "campaign") state.campaigns = []
          else if (level === "objective") state.objectives = []
          else state.ads = []
          state.pagingByLevel[level] = emptyPaidPaging()
          state.paging = emptyPaidPaging()
        }
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientPaidPerformance } = clientPaidPerformanceSlice.actions
export default clientPaidPerformanceSlice.reducer
